import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { authAPI } from '../utils/authUtils';
import './logo.css';
import { FaHome, FaInfoCircle, FaServicestack, FaEnvelope, FaBars, FaTimes, FaHandshake } from 'react-icons/fa';

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const authenticated = authAPI.hasRole('admin') || authAPI.hasRole('superadmin');

  const logoWrapperRef = useRef(null);
  const logoImgRef = useRef(null);
  const navRootRef = useRef(null);
  const [showDesktop, setShowDesktop] = useState(true);

  useEffect(() => {
    let timer = null;
    const measure = () => {
      const navRoot = navRootRef.current;
      const logoWrapper = logoWrapperRef.current;
      if (!navRoot || !logoWrapper) { setShowDesktop(true); return; }
      const logoBounds = logoWrapper.getBoundingClientRect();
      const navBounds = navRoot.getBoundingClientRect();
      const logoRequiredWidth = logoBounds.width + 40;
      const availableForButtons = navBounds.width - logoRequiredWidth - 60;
      const estimatedButtonsWidth = 450;
      setShowDesktop(availableForButtons >= estimatedButtonsWidth);
    };
    const onResize = () => { clearTimeout(timer); timer = setTimeout(measure, 120); };
    window.addEventListener('resize', onResize);
    measure();
    const initTimer = setTimeout(measure, 400);
    if (document && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => setTimeout(measure, 80)).catch(() => {});
    }
    return () => { window.removeEventListener('resize', onResize); clearTimeout(timer); clearTimeout(initTimer); };
  }, []);

  const location = useLocation();
  const navLinks = [
    { name: 'Home', path: '/', icon: <FaHome /> },
    { name: 'Partners', path: '/partners-list', icon: <FaHandshake /> },
    { name: 'Contact us', path: '/contact', icon: <FaEnvelope /> },
  ];

  const [aboutOpen, setAboutOpen] = useState(false);
  const [teamSubmenuOpen, setTeamSubmenuOpen] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const aboutRef = useRef(null);
  const scopeRef = useRef(null);

  useEffect(() => {
    if (!aboutOpen) return;
    function handleClick(e) { if (aboutRef.current && !aboutRef.current.contains(e.target)) setAboutOpen(false); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [aboutOpen]);

  useEffect(() => {
    if (!scopeOpen) return;
    function handleClick(e) { if (scopeRef.current && !scopeRef.current.contains(e.target)) setScopeOpen(false); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [scopeOpen]);

  const aboutDropdown = [
    { name: 'Our Story', path: '/about' },
    { name: 'Meet Our Team', submenu: [ { name: 'Founders', path: '/team' }, { name: 'Advisors', path: '/advisors' } ] },
    { name: 'Legacy For Impacts', path: '/projects' },
  ];

  const scopeDropdown = [
    { name: 'Services', path: '/services' },
    { name: 'Events', path: '/events' },
    { name: 'Publications', path: '/articles' },
  ];

  return (
    <motion.nav
      initial={{ opacity: 0, y: -100 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -100 }}
      transition={{ duration: 0.7 }}
      className="sticky top-0 z-50 w-full sm:w-[95%] mx-auto backdrop-blur-lg bg-[#2F3A36]/90 shadow-xl transition-all duration-500 rounded-b-3xl"
      ref={navRootRef}
    >
      <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20 min-h-[80px] gap-3">
          <div className="flex-shrink-0 flex items-center gap-1.5 sm:gap-2 min-w-0 ml-0 sm:-ml-6">
            <Link to="/" className="flex items-center gap-1.5 sm:gap-2 group">
              <div ref={logoWrapperRef} className="logo-wrapper relative inline-block">
                <img ref={logoImgRef} src="/VPED-logo.png" alt="VenturePoint Logo" className="logo h-14 sm:h-16 w-auto object-contain animate-spin-slow group-hover:scale-110 transition-transform duration-500 flex-shrink-0" style={{ maxHeight: '64px' }} />
                <div className="logo-shadow pointer-events-none absolute left-1/2 transform -translate-x-1/2" />
              </div>
              <span className="flex flex-col leading-tight whitespace-nowrap" style={{letterSpacing: '0.03em'}}>
                <span className="text-base sm:text-lg md:text-xl lg:text-2xl font-extrabold tracking-wide text-[#ffffff] drop-shadow-lg animate-gradient-x">VenturePoint</span>
                <span className="text-xs sm:text-xs md:text-sm font-semibold text-[#3a8b9e] -mt-0.5">for Economic Development</span>
              </span>
            </Link>
          </div>

          {showDesktop && (
            <div className="flex items-center gap-0.5">
              {(() => {
                const link = navLinks[0];
                const isActive = location.pathname === link.path;
                return (
                  <Link key={link.name} to={link.path} className={`flex items-center gap-1.5 text-base font-medium px-2.5 py-2 rounded-md transition-all duration-300 relative whitespace-nowrap ${isActive ? 'text-[#3a8b9e] font-bold' : 'text-gray-50 hover:text-[#3a8b9e]'} group`}>
                    <span className={`transition-transform duration-300 text-base flex-shrink-0 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`}>{link.icon}</span>
                    <span className="text-base">{link.name}</span>
                    {isActive && <span className="absolute left-0 bottom-0 w-full h-1 bg-[#3a8b9e] rounded-full animate-underline" />}
                  </Link>
                );
              })()}

              <div className="relative group" ref={aboutRef}>
                <button
                  className={`flex items-center gap-1.5 lg:gap-2 px-2 lg:px-3 py-2 rounded-md text-base font-medium transition-all duration-300 relative whitespace-nowrap flex-shrink-0 ${['/about','/team','/projects','/advisors'].includes(location.pathname) ? 'text-[#3a8b9e] font-bold' : 'text-gray-50 hover:text-[#3a8b9e]'} group focus:outline-none`}
                  aria-haspopup="true"
                  aria-expanded={aboutOpen}
                  onClick={(e) => { e.preventDefault(); setAboutOpen(v => !v); }}
                  type="button"
                >
                  <FaInfoCircle className="transition-transform duration-300 group-hover:scale-110 text-base flex-shrink-0" />
                  <span className="text-base">About Us</span>
                  <svg className={`w-3 h-3 transition-transform duration-200 flex-shrink-0 ${aboutOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                  {['/about','/team','/projects','/advisors'].includes(location.pathname) && <span className="absolute left-0 bottom-0 w-full h-1 bg-[#3a8b9e] rounded-full animate-underline" />}
                </button>

                {aboutOpen && (
                  <div className="absolute left-0 mt-2 w-56 bg-slate-900/95 border border-gray-700/30 rounded-xl shadow-xl py-2 z-50 animate-fade-in">
                    {aboutDropdown.map((item) => (
                      item.submenu ? (
                        <div key={item.name} className="relative group">
                          <button className="w-full text-left px-5 py-2 text-base rounded-lg transition-all duration-200 hover:bg-[#3a8b9e]/20 hover:text-[#3a8b9e] text-gray-50 flex items-center justify-between" onClick={() => setTeamSubmenuOpen(!teamSubmenuOpen)}>
                            <span>{item.name}</span>
                            <svg className={`w-3 h-3 transition-transform ${teamSubmenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                          </button>

                          {teamSubmenuOpen && (
                            <div className="bg-slate-800/80 ml-6 mt-1 rounded-lg overflow-hidden">
                              {item.submenu.map((subitem) => (
                                <Link key={subitem.name} to={subitem.path} className={`block px-5 py-2 text-base rounded-lg transition-all duration-200 hover:bg-[#3a8b9e]/20 hover:text-[#3a8b9e] ${location.pathname === subitem.path ? 'text-[#3a8b9e] font-semibold' : 'text-gray-50'}`} onClick={() => { setAboutOpen(false); setTeamSubmenuOpen(false); }}>
                                  {subitem.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <Link key={item.name} to={item.path} className={`block px-5 py-2 text-base rounded-lg transition-all duration-200 hover:bg-[#3a8b9e]/20 hover:text-[#3a8b9e] ${location.pathname === item.path ? 'text-[#3a8b9e] font-semibold' : 'text-gray-50'}`} onClick={() => setAboutOpen(false)}>
                          {item.name}
                        </Link>
                      )
                    ))}
                  </div>
                )}
              </div>

              <div className="relative group" ref={scopeRef}>
                <button className={`flex items-center gap-1.5 lg:gap-2 px-2 lg:px-3 py-2 rounded-md text-base font-medium transition-all duration-300 relative whitespace-nowrap flex-shrink-0 ${['/services', '/events', '/articles'].includes(location.pathname) ? 'text-[#3a8b9e] font-bold' : 'text-gray-50 hover:text-[#3a8b9e]'} group focus:outline-none`} aria-haspopup="true" aria-expanded={scopeOpen} onClick={(e) => { e.preventDefault(); setScopeOpen(v => !v); }} type="button" title={window.innerWidth >= 768 ? 'Scope' : ''}>
                  <FaServicestack className="transition-transform duration-300 group-hover:scale-110 text-base lg:text-xl flex-shrink-0" />
                  {window.innerWidth >= 1024 && <span>Scope</span>}
                  <svg className={`w-3 h-3 transition-transform duration-200 flex-shrink-0 ${aboutOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>

                  {['/services', '/events', '/articles'].includes(location.pathname) && <span className="absolute left-0 bottom-0 w-full h-1 bg-[#3a8b9e] rounded-full animate-underline" />}
                </button>

                {scopeOpen && (
                  <div className="absolute left-0 mt-2 w-48 bg-slate-900/95 border border-gray-700/30 rounded-xl shadow-xl py-2 z-50 animate-fade-in">
                    {scopeDropdown.map((item) => (
                      <Link key={item.name} to={item.path} className={`block px-5 py-2 text-base rounded-lg transition-all duration-200 hover:bg-[#3a8b9e]/20 hover:text-[#3a8b9e] ${location.pathname === item.path ? 'text-[#3a8b9e] font-semibold' : 'text-gray-50'}`} onClick={() => setScopeOpen(false)}>
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {navLinks.slice(1).map((link) => {
                const isActive = location.pathname === link.path;
                const isContactUs = link.name === 'Contact us';
                return (
                  <Link key={link.name} to={link.path} className={`flex items-center gap-1.5 px-2.5 py-2 rounded-md text-base font-medium transition-all duration-300 relative whitespace-nowrap ${isContactUs ? 'bg-[#3a8b9e] text-white hover:bg-[#3a8b9e] shadow-md' : isActive ? 'text-[#3a8b9e] font-bold' : 'text-gray-50 hover:text-[#3a8b9e]'} group`}>
                    <span className={`transition-transform duration-300 text-base flex-shrink-0 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`}>{link.icon}</span>
                    <span className="text-base">{link.name}</span>
                    {isActive && !isContactUs && <span className="absolute left-0 bottom-0 w-full h-1 bg-[#3a8b9e] rounded-full animate-underline" />}
                  </Link>
                );
              })}
            </div>
          )}

          {!showDesktop && (
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-[#3a8b9e] hover:text-gray-50 focus:outline-none focus:text-[#3a8b9e] transition-transform duration-300 flex-shrink-0" aria-label="Toggle menu">
              {isMenuOpen ? <FaTimes className="h-7 w-7 sm:h-8 sm:w-8 animate-fade-in" /> : <FaBars className="h-7 w-7 sm:h-8 sm:w-8 animate-fade-in" />}
            </button>
          )}
        </div>
      </div>

      {isMenuOpen && !showDesktop && (
        <div className="animate-slide-down">
          <div className="px-4 pt-2 pb-3 space-y-1 bg-[#2F3A36]/95 backdrop-blur-lg shadow-lg rounded-b-xl">
            {(() => {
              const link = navLinks[0];
              const isActive = location.pathname === link.path;
              return (
                <Link key={link.name} to={link.path} className={`flex items-center gap-3 text-sm font-medium px-3 py-2 rounded-md transition-all duration-300 relative ${isActive ? 'bg-slate-900/40 text-[#3a8b9e] font-bold' : 'text-gray-50 hover:text-[#3a8b9e]'} group`} onClick={() => setIsMenuOpen(false)}>
                  <span className={`transition-transform duration-300 text-xl flex-shrink-0 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`}>{link.icon}</span>
                  {link.name}
                  {isActive && <span className="absolute left-0 bottom-0 w-full h-1 bg-[#3a8b9e] rounded-full animate-underline" />}
                </Link>
              );
            })()}

            <div className="relative">
              <button className={`flex items-center gap-3 text-base font-medium px-3 py-2 rounded-md transition-all duration-300 relative ${['/about','/team','/projects','/advisors'].includes(location.pathname) ? 'text-[#3a8b9e] font-bold' : 'text-gray-50 hover:text-[#3a8b9e]'} group focus:outline-none`} aria-haspopup="true" aria-expanded={false} onClick={() => setAboutOpen(v => !v)}>
                <FaInfoCircle className="transition-transform duration-300 group-hover:scale-110 text-xl flex-shrink-0" />
                About Us
                <svg className={`ml-auto w-3 h-3 transition-transform duration-200 flex-shrink-0 ${false ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                {['/about','/team','/projects','/advisors'].includes(location.pathname) && <span className="absolute left-0 bottom-0 w-full h-1 bg-[#3a8b9e] rounded-full animate-underline" />}
              </button>

              <div className="mt-2 ml-10 space-y-1 animate-fade-in">
                {aboutDropdown.map((item) => (
                  item.submenu ? (
                    <div key={item.name} className="relative">
                      <button className="w-full text-left px-4 py-2 text-sm rounded-lg transition-all duration-200 hover:bg-[#3a8b9e]/20 hover:text-[#3a8b9e] text-gray-50 flex items-center justify-between" onClick={() => setTeamSubmenuOpen(!teamSubmenuOpen)}>
                        <span>{item.name}</span>
                        <svg className={`w-3 h-3 transition-transform ${teamSubmenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      {teamSubmenuOpen && (
                        <div className="mt-1 ml-4 space-y-1">
                          {item.submenu.map((subitem) => (
                            <Link key={subitem.name} to={subitem.path} className={`block px-4 py-2 text-sm rounded-lg transition-all duration-200 hover:bg-[#3a8b9e]/20 hover:text-[#3a8b9e] ${location.pathname === subitem.path ? 'text-[#3a8b9e] font-semibold bg-[#3a8b9e]/10' : 'text-gray-50'}`} onClick={() => { setAboutOpen(false); setTeamSubmenuOpen(false); setIsMenuOpen(false); }}>
                              {subitem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link key={item.name} to={item.path} className={`block px-4 py-2 text-sm rounded-lg transition-all duration-200 hover:bg-[#3a8b9e]/20 hover:text-[#3a8b9e] ${location.pathname === item.path ? 'text-[#3a8b9e] font-semibold bg-[#3a8b9e]/10' : 'text-gray-50'}`} onClick={() => { setAboutOpen(false); setIsMenuOpen(false); }}>
                      {item.name}
                    </Link>
                  )
                ))}
              </div>
            </div>

            <div className="relative">
              <button className={`flex items-center gap-3 w-full px-3 py-2 rounded-md text-base font-medium transition-all duration-300 relative ${['/services', '/events', '/articles'].includes(location.pathname) ? 'text-[#3a8b9e] font-bold' : 'text-gray-50 hover:text-[#3a8b9e]'} group focus:outline-none`} aria-haspopup="true" aria-expanded={false} onClick={() => {}}>
                <FaServicestack className="transition-transform duration-300 group-hover:scale-110 text-xl flex-shrink-0" />
                Scope
                {['/services', '/events', '/articles'].includes(location.pathname) && <span className="absolute left-0 bottom-0 w-full h-1 bg-[#3a8b9e] rounded-full animate-underline" />}
              </button>

              <div className="mt-2 ml-8 space-y-1 animate-fade-in">
                {scopeDropdown.map((item) => (
                  <Link key={item.name} to={item.path} className={`block px-4 py-2 text-base rounded-lg transition-all duration-200 hover:bg-[#3a8b9e]/20 hover:text-[#3a8b9e] ${location.pathname === item.path ? 'text-[#3a8b9e] font-semibold' : 'text-gray-50'}`} onClick={() => { setIsMenuOpen(false); }}>
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {navLinks.slice(1).map((link) => {
              const isActive = location.pathname === link.path;
              const isContactUs = link.name === 'Contact us';
              return (
                <Link key={link.name} to={link.path} className={`flex items-center gap-3 text-sm font-medium px-3 py-2 rounded-md transition-all duration-300 relative ${isContactUs ? 'bg-[#3a8b9e] text-white hover:bg-[#3a8b9e]' : isActive ? 'bg-slate-900/40 text-[#3a8b9e] font-bold' : 'text-gray-50 hover:text-[#3a8b9e]'} group`} onClick={() => setIsMenuOpen(false)}>
                  <span className={`transition-transform duration-300 text-xl flex-shrink-0 ${isActive && !isContactUs ? 'scale-110' : 'group-hover:scale-110'}`}>{link.icon}</span>
                  {link.name}
                  {isActive && !isContactUs && <span className="absolute left-0 bottom-0 w-full h-1 bg-[#3a8b9e] rounded-full animate-underline" />}
                </Link>
              );
            })}

          </div>
        </div>
      )}
    </motion.nav>
  );
}
